"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Calculator, Compass, Hammer, Sparkles, House as Home } from 'lucide-react';
import { Link } from "@/components/common/Link";
interface RelatedItem {
  title: string;
  description: string;
  to: string;
  icon: React.ElementType;
  badge?: string;
}
const relatedItems: RelatedItem[] = [{
  title: "Remodeling Cost Guide",
  description: "Explore neighborhood pricing insights, square-foot benchmarks, and realistic Decatur investment ranges.",
  to: "/DecaturRemodelingCostGuidePage",
  icon: Calculator
}, {
  title: "Remodeling Planning Guide",
  description: "Navigate local zoning rules, historical preservation guidelines, and structured project roadmaps.",
  to: "/DecaturRemodelingPlanningGuidePage",
  icon: Compass
}, {
  title: "Our Renovation Process",
  description: "Learn how our design-build methodology guides projects seamlessly from concept to turnkey handover.",
  to: "/ProcessPlanningPage",
  icon: Hammer
}, {
  title: "Kitchen Remodeling",
  description: "Custom cabinetry, expanded kitchen islands, and chef-grade layouts tailored for contemporary living.",
  to: "/KitchenRemodelingPage",
  icon: Sparkles
}, {
  title: "Whole-Home Remodeling",
  description: "Comprehensive historic home restorations, structural reconfigurations, and full-footprint transformations.",
  to: "/WholeHomeRoomRemodelingPage",
  icon: Home
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function RelatedGuides() {
  return <section id="relatedguidesservices" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header Block */}
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-18">
          <div aria-hidden="true" className="w-12 h-0.5 bg-chart-1 mx-auto mb-4 rounded-full" />
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-4">
            Explore Related Guides &amp; Services
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Essential planning resources, architectural guides, and specialized remodeling services for your Decatur home.
          </p>
        </div>

        {/* Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {relatedItems.map((item, index) => {
          const IconComponent = item.icon;
          const isLastOnWide = index === 4;
          return <motion.div key={item.title} data-index={index} variants={itemVariants} className={`group relative flex flex-col justify-between rounded-xl p-7 md:p-8 bg-card/85 backdrop-blur-md border border-border transition-all duration-300 hover:-translate-y-1 hover:border-chart-1/50 hover:shadow-md ${isLastOnWide ? "md:col-span-2 lg:col-span-1" : ""}`}>
                <div>
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-lg bg-secondary text-foreground flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                    <IconComponent className="size-5" />
                  </div>

                  {/* Title & Description */}
                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-foreground mb-3 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>

                {/* Action Link */}
                <div className="pt-2 border-t border-border/60 mt-auto">
                  <Link to={item.to} className="inline-flex items-center gap-1.5 text-sm font-medium text-chart-1 group-hover:text-foreground transition-colors duration-200">
                    <span>View details</span>
                    <ArrowUpRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}